import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import nexhalaLogo from '@/assets/nexhala_logo.jpg';
import deltaLogo from '@/assets/delta_logo.png';

const partners = [
  {
    id: 'partner-nexhala',
    name: 'Nexhala',
    logo: nexhalaLogo,
    coreBusiness: 'Web, mobile & AI Software development. Digital solutions for startups and enterprises.',
    website: 'https://nexhala.com',
  },
  {
    id: 'partner-delta',
    name: 'Delta Architect',
    logo: deltaLogo,
    coreBusiness: 'Architectural planning, structural design, 2D & 3D visualization, and construction support.',
  }
];

export const PartnersSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="partners" className="py-20 lg:py-28 bg-muted/30" ref={ref}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-primary font-medium uppercase tracking-wider text-sm">
            Our Partners
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mt-4 mb-6">
            Trusted by <span className="text-gradient-gold">Industry Leaders</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            We collaborate with leading brands to deliver quality materials and solutions for your home.
          </p>
        </motion.div>

        {/* Partners Grid: centered when ≤4, horizontal scroll when >4 */}
        <div
          className={
            partners.length <= 4
              ? 'flex flex-wrap justify-center gap-8'
              : 'overflow-x-auto flex flex-nowrap gap-8 pb-4 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0'
          }
        >
          {partners.map((partner, index) => (
            <motion.div
              key={partner.id}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className={
                partners.length <= 4
                  ? 'w-full sm:w-[280px] sm:min-w-[260px] flex-shrink-0'
                  : 'min-w-[280px] flex-shrink-0'
              }
            >
              <Card className="h-full card-premium border-border/50 overflow-hidden group hover:shadow-card-hover transition-shadow">
                <CardContent className="p-6 flex flex-col items-center text-center">
                  {/* Logo */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="w-24 h-24 rounded-xl bg-muted flex items-center justify-center mb-4 overflow-hidden transition-all duration-300"
                  >
                    <img
                      src={partner.logo}
                      alt={`${partner.name} logo`}
                      className="w-full h-full object-contain p-2"
                    />
                  </motion.div>

                  {/* Name */}
                  <h3 className="font-display text-lg font-semibold text-foreground mb-2">
                    {partner.name}
                  </h3>

                  {/* Core Business */}
                  <p className="text-sm text-muted-foreground mb-4 flex-grow">
                    {partner.coreBusiness}
                  </p>

                  {/* Visit Link */}
                  {partner.website && (
                    <a
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary-dark transition-colors"
                    >
                      Visit
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
